import { useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import type * as THREE from "three";
import {
	DEFAULT_SITE_CONDITIONS,
	getTerrainSlopeAtPoint,
	type SiteConditions,
} from "../../domain/foundation/siteConditions";
import { matGround } from "../../engines/materials";
import type { DeckConfig, SaunaDimensions } from "../../engines/types";
import { SceneDressing } from "./SceneEnvironment.dressing";
import {
	createGroundGeometry,
	createGroundOverlayTexture,
	createStudioEnvMap,
	getApproachEdgeZ,
	getEnvironmentIntensityForTime,
	getGroundBaseY,
	getHdriGainMapForTime,
	getHdriUrlForTime,
	type LoadedEnvMap,
	loadHdriEnvMap,
	resolveGroundingFootprint,
	resolveWaterPlacement,
} from "./SceneEnvironment.helpers";
import { SceneForest } from "./SceneForest";
import { SceneGrass } from "./SceneGrass";
import { SceneIndoorFloor } from "./SceneIndoorRoom";
import { SceneLandscape } from "./SceneLandscape";
import { SceneShoreRocks } from "./SceneShoreRocks";
import { SceneSky } from "./SceneSky";
import { SceneWater } from "./SceneWater";
import {
	getTimeOfDayPresentationTarget,
	type TimeOfDayValue,
	type ViewerPresentationProfile,
	type ViewerRuntimeProfile,
} from "./viewerConfig";

interface SceneEnvironmentProps {
	runtimeProfile: ViewerRuntimeProfile;
	presentationProfile: ViewerPresentationProfile;
	dimensions: SaunaDimensions;
	deckConfig?: DeckConfig | null;
	siteConditions?: SiteConditions;
	timeOfDay: TimeOfDayValue;
	/**
	 * Whole-building orientation. The site itself never turns — but everything
	 * drawn AROUND the building (lawn keep-out, grounding halo, lakeside
	 * clearance) has to be derived for the orientation the building stands in.
	 * `dimensions` and `deckConfig` stay in MODEL space; the helpers rotate.
	 */
	rotationDeg?: number;
	/**
	 * Indoor sauna (issue 175): a floor and a dim room instead of the plot —
	 * no sky, terrain, forest, grass or lake. The image-based lighting stays,
	 * the interior needs it either way. The room's walls are model-relative
	 * and live in the sauna rig (`SceneIndoorWalls`).
	 */
	indoor?: boolean;
	interiorView?: boolean;
}

export function SceneEnvironment({
	runtimeProfile,
	presentationProfile,
	dimensions,
	deckConfig,
	siteConditions,
	timeOfDay,
	rotationDeg = 0,
	indoor = false,
	interiorView = false,
}: SceneEnvironmentProps) {
	const sceneReadonly = useThree((state) => state.scene);
	const renderer = useThree((state) => state.gl);
	const invalidate = useThree((state) => state.invalidate);
	const sceneRef = useRef<THREE.Scene | null>(null);
	const resolvedSiteConditions = siteConditions ?? DEFAULT_SITE_CONDITIONS;
	const isEvening = timeOfDay >= 0.75;

	// Single owner of the graded time-of-day presentation target. The sky
	// dome, the distance fog and the dressing hedge's aerial perspective
	// all read the same object, so the planting's haze cannot drift away
	// from the air it is standing in.
	const presentationTarget = useMemo(
		() =>
			getTimeOfDayPresentationTarget(
				timeOfDay,
				presentationProfile,
				runtimeProfile,
			),
		[timeOfDay, presentationProfile, runtimeProfile],
	);

	useEffect(() => {
		sceneRef.current = sceneReadonly;
	}, [sceneReadonly]);

	// Procedural envMap is the immediate fallback. It always renders
	// first (no fetch). On balanced/high tiers a real HDRI swaps in
	// once decoded.
	const proceduralEnvMap = useMemo(
		() =>
			createStudioEnvMap({
				isEvening,
				reflectionBoost: runtimeProfile.reflectionBoost,
			}),
		[isEvening, runtimeProfile.reflectionBoost],
	);

	const [realHdriMap, setRealHdriMap] = useState<LoadedEnvMap | null>(null);

	// Async HDRI loader. SSR-safe (helper short-circuits when document
	// is undefined). Cancellation flag protects against unmount and
	// against day↔evening flips arriving before a previous load
	// resolves. The local `loaded` ref ensures the cancelled-after-
	// resolve case still disposes the orphaned texture.
	useEffect(() => {
		if (!runtimeProfile.useRealHdri) {
			// No setState in the body — the previous run's cleanup
			// already nulled the state when deps flipped, so falling
			// straight through here keeps the lint rule happy and
			// avoids a redundant render.
			return;
		}
		let cancelled = false;
		let loaded: LoadedEnvMap | null = null;
		const url = getHdriUrlForTime(timeOfDay);
		const gainMapAsset = getHdriGainMapForTime(timeOfDay);

		loadHdriEnvMap(url, renderer, gainMapAsset).then((envMap) => {
			if (cancelled) {
				envMap?.dispose();
				return;
			}
			loaded = envMap;
			setRealHdriMap(envMap);
		});

		return () => {
			cancelled = true;
			loaded?.dispose();
			setRealHdriMap(null);
		};
	}, [renderer, runtimeProfile.useRealHdri, timeOfDay]);

	// Gate on useRealHdri at read time too: if the tier flips false
	// while a stale `realHdriMap` is still in state (e.g., the cleanup
	// hasn't flushed yet), we still fall through to the procedural map.
	const activeEnvMap =
		(runtimeProfile.useRealHdri ? realHdriMap?.texture : null) ??
		proceduralEnvMap;

	// Procedural map disposal — own its own lifecycle so swapping in
	// a real HDRI doesn't trigger early disposal of the fallback.
	useEffect(
		() => () => {
			proceduralEnvMap.dispose();
		},
		[proceduralEnvMap],
	);

	// Scene attachment. Decoupled from disposal: this effect only
	// assigns scene.environment and nullifies it
	// on unmount. The textures clean themselves up via the dedicated
	// effects above.
	useEffect(() => {
		const scene = sceneRef.current;
		if (!scene) return;

		scene.background = null;
		scene.environment = activeEnvMap;

		return () => {
			if (scene.environment === activeEnvMap) {
				scene.environment = null;
			}
		};
	}, [activeEnvMap]);

	// Damp the IBL contribution globally — the real HDRI on balanced/high
	// tiers was over-filling the cream interior aspen and reading as
	// blown-out — and let it fall away across dusk with the direct lights
	// (see getEnvironmentIntensityForTime). Glass / metal still get strong
	// reflections via their own envMapIntensity bump. Three.js r163+
	// feature; older runtimes ignore it harmlessly.
	const environmentIntensity = getEnvironmentIntensityForTime(
		timeOfDay,
		interiorView,
	);
	useEffect(() => {
		const scene = sceneRef.current as
			| (THREE.Scene & { environmentIntensity?: number })
			| null;
		if (!scene || !("environmentIntensity" in scene)) return;
		scene.environmentIntensity = environmentIntensity;
		invalidate();
	}, [environmentIntensity, invalidate]);

	const groundGeometry = useMemo(
		() => createGroundGeometry(resolvedSiteConditions),
		[resolvedSiteConditions],
	);

	useEffect(
		() => () => {
			groundGeometry.dispose();
		},
		[groundGeometry],
	);

	const groundingFootprint = useMemo(
		() => resolveGroundingFootprint(dimensions, deckConfig, rotationDeg),
		[dimensions, deckConfig, rotationDeg],
	);

	const shadowTexture = useMemo(
		() =>
			createGroundOverlayTexture({
				coreColor: isEvening ? 0x15120d : 0x2a241a,
				edgeColor: 0x000000,
				falloff: 1.85,
			}),
		[isEvening],
	);

	const earthHaloTexture = useMemo(
		() =>
			createGroundOverlayTexture({
				coreColor: isEvening ? 0x3a2d20 : 0x80694f,
				edgeColor: isEvening ? 0x1b1713 : 0x433b32,
				falloff: 1.35,
			}),
		[isEvening],
	);

	useEffect(
		() => () => {
			shadowTexture.dispose();
			earthHaloTexture.dispose();
		},
		[shadowTexture, earthHaloTexture],
	);

	const centerGroundY =
		getGroundBaseY(resolvedSiteConditions) +
		getTerrainSlopeAtPoint(resolvedSiteConditions, 0, 0);
	const showGrounding =
		runtimeProfile.contactShadowStrength > 0.08 &&
		resolvedSiteConditions.terrainMode !== "slope";
	// When real `<ContactShadows>` are also rendering under the model
	// the static earth-halo overlays double up and the model reads as
	// over-grounded. Drop the overlay opacity to ~35 % in that case so
	// the warm earth tone still tints the surrounding ground but the
	// hard contact shape comes from the real shadow pass.
	const overlayDamping = runtimeProfile.contactShadowsEnabled ? 0.35 : 1.0;

	const approachEdgeZ = useMemo(
		() =>
			getApproachEdgeZ({
				saunaLength: dimensions.length,
				saunaWidth: dimensions.width,
				deckConfig,
				rotationDeg,
			}),
		[dimensions.length, dimensions.width, deckConfig, rotationDeg],
	);

	const waterPlacement = useMemo(
		() =>
			resolveWaterPlacement(dimensions, deckConfig, centerGroundY, rotationDeg),
		[dimensions, deckConfig, centerGroundY, rotationDeg],
	);

	if (indoor) return <SceneIndoorFloor />;

	return (
		<>
			<SceneSky target={presentationTarget} />
			<mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
				<primitive object={groundGeometry} attach="geometry" />
				<primitive object={matGround} attach="material" />
			</mesh>

			{showGrounding ? (
				<>
					<mesh
						rotation={[-Math.PI / 2, 0, 0]}
						position={[0, centerGroundY + 0.008, 0]}
						renderOrder={1}
					>
						<planeGeometry
							args={[
								groundingFootprint.haloWidth,
								groundingFootprint.haloLength,
							]}
						/>
						<meshBasicMaterial
							map={earthHaloTexture}
							transparent
							opacity={
								(0.12 + runtimeProfile.contactShadowStrength * 0.16) *
								overlayDamping
							}
							depthWrite={false}
						/>
					</mesh>
					<mesh
						rotation={[-Math.PI / 2, 0, 0]}
						position={[0, centerGroundY + 0.012, 0]}
						renderOrder={2}
					>
						<planeGeometry
							args={[groundingFootprint.width, groundingFootprint.length]}
						/>
						<meshBasicMaterial
							map={shadowTexture}
							transparent
							opacity={
								(0.18 + runtimeProfile.contactShadowStrength * 0.34) *
								overlayDamping
							}
							depthWrite={false}
						/>
					</mesh>
				</>
			) : null}

			<SceneLandscape
				footprint={groundingFootprint}
				groundY={centerGroundY}
				site={resolvedSiteConditions}
			/>

			<SceneForest
				tier={runtimeProfile.tier}
				groundY={centerGroundY}
				site={resolvedSiteConditions}
				timeOfDay={timeOfDay}
			/>

			<SceneDressing
				enabled={runtimeProfile.dressingEnabled}
				approachEdgeZ={approachEdgeZ}
				groundY={centerGroundY}
			/>

			<SceneGrass
				enabled={runtimeProfile.instancedGrassEnabled}
				density={runtimeProfile.grassDensity}
				footprint={groundingFootprint}
				groundY={centerGroundY}
			/>

			<SceneWater
				enabled={runtimeProfile.waterEnabled}
				reflections={runtimeProfile.waterReflectionsEnabled}
				detail={runtimeProfile.waterDetailEnabled}
				placement={waterPlacement}
				isEvening={isEvening}
				ambientMotion={runtimeProfile.presentationMode === "cinematic"}
			/>

			{runtimeProfile.waterEnabled ? (
				<SceneShoreRocks
					placement={waterPlacement}
					groundY={centerGroundY}
					site={resolvedSiteConditions}
				/>
			) : null}
		</>
	);
}
